import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, 
  Bell, 
  X, 
  Coins, 
  Gamepad2, 
  Home, 
  Trophy, 
  User, 
  Settings, 
  LogOut, 
  ChevronRight, 
  Menu 
} from 'lucide-react';
import { useNavigate, Link, NavLink, useLocation } from 'react-router-dom';
import { apiRequest, BASE_URL } from '../utils/api';
import trapAndGooImg from '../assets/trap-and-goo.webp';
import forestRushImg from '../assets/forest-rush.webp';
import draugutsImg from '../assets/drauguts.jpg';
import frontlineImg from '../assets/frontline.webp';
import cyrusBeanJumpImg from '../assets/cyrus-bean-jump.webp';
import dandoRushImg from '../assets/dando-rush.webp';
import draughtsImg from '../assets/draughts.webp';
import gunWarsImg from '../assets/gun-wars.webp';
import omiImg from '../assets/omi.webp';
import poolLeagueImg from '../assets/pool-league.webp';
import carromLeagueImg from '../assets/carrom-league.webp';
import defaultAvatar from '../assets/default-avatar.webp';
import logo192 from '../assets/logo192.png';
import AvatarImg from './AvatarImg';

const THUMBNAIL_MAP = {
  "trap-and-goo.jpg": trapAndGooImg,
  "trap-and-goo.webp": trapAndGooImg,
  "forest-rush.jpg": forestRushImg,
  "forest-rush.webp": forestRushImg,
  "drauguts.jpg": draugutsImg,
  "drauguts.webp": draugutsImg,
  "draughts.jpg": draughtsImg,
  "draughts.webp": draughtsImg,
  "frontline.jpg": frontlineImg,
  "frontline.webp": frontlineImg,
  "cyrus-bean-jump.webp": cyrusBeanJumpImg,
  "dando-rush.webp": dandoRushImg,
  "gun-wars.webp": gunWarsImg,
  "omi.webp": omiImg,
  "pool-league.webp": poolLeagueImg,
  "carrom-league.webp": carromLeagueImg,
  "Trap and Goo": trapAndGooImg,
  "Forest Rush": forestRushImg,
  "Draughts": draughtsImg,
  "Frontline": frontlineImg,
  "Cyrus Bean Jump": cyrusBeanJumpImg,
  "Dando Rush": dandoRushImg,
  "Gun Wars": gunWarsImg,
  "Omi": omiImg,
  "Pool League": poolLeagueImg,
  "Carrom League": carromLeagueImg,
};

const NAV_ITEMS = [
  { name: 'Home', path: '/', icon: Home },
  { name: 'Library', path: '/library', icon: Gamepad2 },
  { name: 'Leaderboard', path: '/leaderboard', icon: Trophy },
  { name: 'Profile', path: '/profile', icon: User },
  { name: 'Settings', path: '/settings', icon: Settings },
];

const Navbar = ({ isLoggedIn, onLogout }) => {
  const [userData, setUserData] = useState(null);
  const [coinBalance, setCoinBalance] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);
  const token = localStorage.getItem('accessToken');

  // 1. Fetch User Data & Coins for Navbar
  useEffect(() => {
    const fetchNavData = async () => {
      if (isLoggedIn && token) {
        try {
          // Fetch Profile
          const userRes = await apiRequest('/players/me');
          if (userRes.ok) {
            const data = await userRes.json();
            setUserData(data);
          }

          // Fetch Coin Balance
          const coinRes = await apiRequest('/Coins/balance');
          if (coinRes.ok) {
            const coinData = await coinRes.json();
            setCoinBalance(coinData.coinBalance);
          }
        } catch (error) {
          console.error("Navbar data fetch failed:", error);
        }
      }
    };
    fetchNavData();

    // Listen for custom event to refresh coins
    const handleCoinUpdate = (e) => setCoinBalance(e.detail);
    window.addEventListener('coin-update', handleCoinUpdate);

    // Listen for custom event to refresh avatar in real-time
    const handleAvatarUpdate = (e) => {
      setUserData((prev) => prev ? { ...prev, avatarUrl: e.detail } : prev);
    };
    window.addEventListener('avatar-update', handleAvatarUpdate);

    return () => {
      window.removeEventListener('coin-update', handleCoinUpdate);
      window.removeEventListener('avatar-update', handleAvatarUpdate);
    };
  }, [isLoggedIn, token]);

  // 2. Search Games Logic
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      const q = searchQuery.trim().toLowerCase();
      if (q.length >= 1) {
        setIsSearching(true);
        try {
          const response = await apiRequest('/Games/library');
          if (response.ok) {
            const data = await response.json();
            const filtered = data
              .filter(
                (game) =>
                  game.title?.toLowerCase().includes(q) ||
                  game.genre?.toLowerCase().includes(q)
              )
              .map((game) => ({
                id: game.id,
                title: game.title,
                category: game.genre,
                buildPath: game.launchUrl || '',
                multiplayerType: game.multiplayerType || '',
                image:
                  (game.thumbnailUrl && THUMBNAIL_MAP[game.thumbnailUrl]) ||
                  THUMBNAIL_MAP[game.title] ||
                  null,
              }));
            setSearchResults(filtered);
            setShowResults(true);
          }
        } catch (error) {
          console.error("Game search failed:", error);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 200);
    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const handleGameSelect = (game) => {
    setShowResults(false);
    setSearchQuery('');
    navigate('/library', { state: { selectedGame: game } });
  };

  const handleLogoutClick = async () => {
    const activeToken = localStorage.getItem('accessToken');
    try {
      if (activeToken) {
        await fetch(`${BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${activeToken}`,
            'Content-Type': 'application/json',
            'X-Api-Key': 'eca9a7ef-3b2d-4f8a-9c1e-d72b05f6a340'
          }
        });
      }
    } catch (err) {
      console.error("Logout API error:", err);
    } finally {
      if (onLogout) {
        onLogout();
      } else {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-[#0E0E11]/95 backdrop-blur-md border-b border-[#23232B] transition-all w-full">
      <div className="max-w-[1700px] mx-auto px-2.5 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-1.5 sm:gap-4">
        
        {/* Left: Logo */}
        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          <Link to="/" className="flex items-center gap-1.5 sm:gap-2.5 group">
            <img 
              src={logo192} 
              alt="KarnoGames" 
              className="w-7 h-7 sm:w-10 sm:h-10 md:w-11 md:h-11 object-contain drop-shadow-[0_0_12px_rgba(255,106,0,0.5)] group-hover:scale-105 transition-transform" 
            />
            <div className="flex items-center">
              <span className="font-heading font-black text-sm sm:text-xl md:text-2xl tracking-wider text-white uppercase">
                KARNO
              </span>
              <span className="font-heading font-black text-sm sm:text-xl md:text-2xl tracking-wider text-[#FF6A00] uppercase">
                GAMES
              </span>
            </div>
          </Link>
        </div>

        {/* Center: Navigation Links (Horizontal inline as shown in screenshot) */}
        <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <NavLink
                key={item.name}
                to={item.path}
                className={`relative px-3.5 py-2 flex items-center gap-2 rounded-lg text-xs xl:text-sm font-semibold transition-all duration-200 ${
                  isActive
                    ? 'text-[#FF7A00]'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon 
                  size={17} 
                  className={isActive ? 'text-[#FF7A00]' : 'text-gray-400'} 
                />
                <span>{item.name}</span>
                {isActive && (
                  <span className="absolute bottom-0 left-3 right-3 h-[2px] bg-gradient-to-r from-[#FF5E0E] to-[#FFB800] rounded-full shadow-[0_0_8px_rgba(255,122,0,0.8)]" />
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Right Section: Search + Notifications + Avatar + Login/Logout */}
        <div className="flex items-center gap-1 sm:gap-3 md:gap-4 shrink-0">
          
          {/* Search Pill Input */}
          <div className="relative hidden md:block w-48 lg:w-60 xl:w-72" ref={searchRef}>
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
              <input 
                type="text" 
                placeholder="Search games..." 
                className="w-full bg-[#18181F] border border-[#2B2B36] rounded-full py-2 pl-9 pr-9 text-xs text-white placeholder-gray-500 outline-none focus:border-[#FF7A00] focus:shadow-[0_0_12px_rgba(255,122,0,0.25)] transition-all"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.trim().length >= 1 && setShowResults(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (searchResults.length > 0) {
                      handleGameSelect(searchResults[0]);
                    } else {
                      navigate('/library');
                      setShowResults(false);
                    }
                  }
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setShowResults(false);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Search Dropdown */}
            {showResults && (
              <div className="absolute top-full left-0 w-full mt-2 bg-[#1A1A22] border border-[#2E2E3C] rounded-xl shadow-[0_20px_40px_rgba(0,0,0,0.9)] max-h-80 overflow-y-auto z-[100] divide-y divide-[#2B2B38]">
                {isSearching ? (
                  <div className="p-3 text-center text-xs text-gray-400">
                    Searching...
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="p-3 text-center text-xs text-gray-400">
                    No games found
                  </div>
                ) : (
                  searchResults.map((game) => (
                    <div
                      key={game.id}
                      onClick={() => handleGameSelect(game)}
                      className="flex items-center gap-3 p-2.5 hover:bg-white/5 cursor-pointer group transition-colors"
                    >
                      <div className="w-12 h-8 bg-black rounded overflow-hidden shrink-0 border border-white/10">
                        {game.image ? (
                          <img src={game.image} alt={game.title} className="w-full h-full object-cover" />
                        ) : (
                          <Gamepad2 size={16} className="text-gray-400 m-auto" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-heading font-bold text-xs uppercase text-white group-hover:text-[#FF7A00] truncate">
                          {game.title}
                        </div>
                        {game.category && (
                          <div className="text-[10px] text-gray-400">
                            {game.category}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Coin Balance (when logged in) */}
          {isLoggedIn && (
            <div className="hidden sm:flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/30 px-3 py-1.5 rounded-full shadow-[0_0_10px_rgba(245,158,11,0.15)]">
              <Coins size={15} className="text-amber-400" />
              <span className="font-black text-xs text-amber-400">{coinBalance.toLocaleString()}</span>
            </div>
          )}

          {/* Bell Notifications */}
          <Link 
            to="/notifications" 
            className="w-7 h-7 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-gray-300 hover:text-white hover:bg-white/5 relative transition-colors shrink-0"
            title="Notifications"
          >
            <Bell size={16} className="sm:w-5 sm:h-5" />
            <span className="absolute top-1 right-1 sm:top-1.5 sm:right-1.5 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#FF4500] rounded-full shadow-[0_0_8px_rgba(255,69,0,1)]"></span>
          </Link>

          {/* Avatar Icon / Profile */}
          <button 
            onClick={() => navigate(isLoggedIn ? '/profile' : '/login')}
            className="w-7 h-7 sm:w-9 sm:h-9 rounded-full p-0.5 border-2 border-[#FF7A00] overflow-hidden hover:scale-105 transition-transform shrink-0 shadow-[0_0_10px_rgba(255,122,0,0.3)]"
            title={isLoggedIn ? (userData?.gamerName || 'Profile') : 'Login'}
          >
            <AvatarImg
              src={userData?.avatarUrl || userData?.avatarId}
              alt="Avatar"
              className="w-full h-full object-cover rounded-full"
              fallback={
                <img src={defaultAvatar} alt="Avatar" className="w-full h-full object-cover rounded-full" />
              }
            />
          </button>

          {/* Login Button (orange pill with arrow as in screenshot) or Logout */}
          {isLoggedIn ? (
            <button 
              onClick={handleLogoutClick}
              className="flex items-center gap-1 bg-red-500/15 hover:bg-red-500/25 border border-red-500/40 text-red-400 px-2.5 sm:px-4 py-1 sm:py-2 rounded-full font-bold text-[11px] sm:text-xs uppercase tracking-wider transition-all shrink-0 whitespace-nowrap"
              title="Logout"
            >
              <LogOut size={12} className="sm:w-3.5 sm:h-3.5" />
              <span>Logout</span>
            </button>
          ) : (
            <Link 
              to="/login"
              className="flex items-center gap-0.5 sm:gap-1 bg-gradient-to-r from-[#FF5E0E] to-[#FF8A00] hover:from-[#FF6E1E] hover:to-[#FF9A10] text-white px-2.5 sm:px-5 py-1 sm:py-2 rounded-full font-bold text-xs sm:text-sm tracking-wide shadow-[0_0_15px_rgba(255,94,14,0.4)] transition-all hover:scale-105 active:scale-95 shrink-0 whitespace-nowrap"
            >
              <span>Login</span>
              <ChevronRight size={13} className="sm:w-4 sm:h-4" />
            </Link>
          )}

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-1 sm:p-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 shrink-0"
            aria-label="Toggle Navigation"
          >
            {isMobileMenuOpen ? <X size={19} className="sm:w-5 sm:h-5" /> : <Menu size={19} className="sm:w-5 sm:h-5" />}
          </button>

        </div>
      </div>

      {/* Mobile Navigation Dropdown */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-[#121217] border-b border-[#282834] px-4 py-3 space-y-2 animate-in fade-in duration-200">
          {/* Mobile Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
            <input 
              type="text" 
              placeholder="Search games..." 
              className="w-full bg-[#1A1A22] border border-[#2B2B38] rounded-full py-2 pl-9 pr-4 text-xs text-white placeholder-gray-500 outline-none"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  navigate('/library');
                  setIsMobileMenuOpen(false);
                }
              }}
            />
          </div>

          {/* Mobile Nav Links */}
          <div className="flex flex-col gap-1">
            {NAV_ITEMS.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                    isActive
                      ? 'bg-[#FF7A00]/15 text-[#FF7A00] border border-[#FF7A00]/30'
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon size={18} className={isActive ? 'text-[#FF7A00]' : 'text-gray-400'} />
                  <span>{item.name}</span>
                </NavLink>
              );
            })}
          </div>

          {/* Mobile Logout if logged in */}
          {isLoggedIn && (
            <div className="pt-2 border-t border-white/10">
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleLogoutClick();
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-lg font-semibold"
              >
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      )}
    </header>
  );
};

export default Navbar;
